import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { siListAssessments, siCreateAssessment, siDemoSample } from "../lib/smartInspectApi";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Plus, FlaskConical, Loader2, ClipboardList } from "lucide-react";

export default function SmartInspectRoiAssessmentsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try { setRows(await siListAssessments()); }
    catch { toast.error("Failed to load assessments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createNew = async (mode) => {
    setCreating(true);
    try {
      let answers = {};
      if (mode === "demo") { const s = await siDemoSample(); answers = s.answers || {}; }
      const a = await siCreateAssessment(mode, answers);
      toast.success(mode === "demo" ? "Demo assessment created" : "Assessment created");
      nav(`/admin/smartinspect/roi-assessments/${a.id}`);
    } catch { toast.error("Could not create assessment"); }
    finally { setCreating(false); }
  };

  return (
    <div data-testid="si-assessments-page" className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList size={22} className="text-indigo-600" /> SmartInspect ROI Assessments</h1>
          <p className="text-sm text-slate-500 mt-1">Create, resume and manage ROI assessments (consultant, admin and demo).</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="si-new-demo-btn" variant="outline" onClick={() => createNew("demo")} disabled={creating}>
            {creating ? <Loader2 size={16} className="mr-1 animate-spin" /> : <FlaskConical size={16} className="mr-1" />} New Demo
          </Button>
          <Button data-testid="si-new-assessment-btn" onClick={() => createNew("consultant")} disabled={creating}>
            <Plus size={16} className="mr-1" /> New Assessment
          </Button>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="si-assessments-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Company</th>
              <th className="text-left px-4 py-3">Country</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Annual Volume</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Mode</th>
              <th className="text-left px-4 py-3">Created By</th>
              <th className="text-left px-4 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No assessments yet. Create one to get started.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`si-assessment-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => nav(`/admin/smartinspect/roi-assessments/${r.id}`)}>
                <td className="px-4 py-3 font-medium text-slate-800">{r.company_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{r.country || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{r.company_type || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{(r.preview?.annual_inspection_volume ?? 0).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${r.status === "inputs_complete" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
                    {r.status === "inputs_complete" ? "Inputs complete" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 capitalize">{r.mode}</td>
                <td className="px-4 py-3 text-slate-500">{r.created_by || "—"}</td>
                <td className="px-4 py-3 text-slate-400">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
